import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  LinearProgress,
  Divider,
  Avatar
} from '@mui/material';
import {
  EventBusy as LeaveIcon,
  CheckCircle as ApprovedIcon,
  Pending as PendingIcon,
  Cancel as RejectedIcon,
  TrendingUp as TrendingIcon
} from '@mui/icons-material';

const LeaveAnalytics = ({ data, meta }) => {
  if (!data) {
    return (
      <Box sx={{ width: '100%', textAlign: 'center', py: 4 }}>
        <Typography variant="h6" color="text.secondary">
          No leave data available
        </Typography>
      </Box>
    );
  }

  const {
    utilizationByType = [],
    byTypeThisMonth = [],
    monthlyTrend = [],
    thisMonthSummary = { approved: 0, pending: 0, rejected: 0 },
    pendingApprovals = 0,
    keyMetrics = { totalLeaveDays: 0 }
  } = data;

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
        Leave Analytics Overview
      </Typography>

      {/* Key Metrics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
            color: 'white',
            borderRadius: 2
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Avatar sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', width: 40, height: 40 }}>
                  <ApprovedIcon sx={{ color: 'white', fontSize: 20 }} />
                </Avatar>
                <TrendingIcon sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: 18 }} />
              </Box>
              <Typography variant="h4" fontWeight={700} sx={{ mb: 1 }}>
                {thisMonthSummary.approved || 0}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 500 }}>
                Approved This Month
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            background: 'linear-gradient(135deg, #ff9800 0%, #ffb74d 100%)',
            color: 'white',
            borderRadius: 2
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Avatar sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', width: 40, height: 40 }}>
                  <PendingIcon sx={{ color: 'white', fontSize: 20 }} />
                </Avatar>
                <TrendingIcon sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: 18 }} />
              </Box>
              <Typography variant="h4" fontWeight={700} sx={{ mb: 1 }}>
                {pendingApprovals || 0}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 500 }}>
                Pending Approvals
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            background: 'linear-gradient(135deg, #f44336 0%, #ef5350 100%)',
            color: 'white',
            borderRadius: 2
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Avatar sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', width: 40, height: 40 }}>
                  <RejectedIcon sx={{ color: 'white', fontSize: 20 }} />
                </Avatar>
                <TrendingIcon sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: 18 }} />
              </Box>
              <Typography variant="h4" fontWeight={700} sx={{ mb: 1 }}>
                {thisMonthSummary.rejected || 0}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 500 }}>
                Rejected This Month
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            background: 'linear-gradient(135deg, #2196f3 0%, #42a5f5 100%)',
            color: 'white',
            borderRadius: 2
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Avatar sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', width: 40, height: 40 }}>
                  <LeaveIcon sx={{ color: 'white', fontSize: 20 }} />
                </Avatar>
                <TrendingIcon sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: 18 }} />
              </Box>
              <Typography variant="h4" fontWeight={700} sx={{ mb: 1 }}>
                {keyMetrics.totalLeaveDays || 0}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 500 }}>
                Total Leave Days
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Leave Utilization by Type */}
      {utilizationByType.length > 0 && (
        <Card sx={{ mb: 3, borderRadius: 2 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
              Leave Utilization by Type
            </Typography>
            <Grid container spacing={2}>
              {utilizationByType.map((item, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Box sx={{
                    p: 2,
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #f5f5f5 0%, #fafafa 100%)'
                  }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {item.type}
                      </Typography>
                      <Chip
                        label={`${item.daysUsed} days`}
                        size="small"
                        sx={{ backgroundColor: '#e3f2fd', color: '#1976d2' }}
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {item.requestCount} requests
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min((item.daysUsed / 30) * 100, 100)}
                      sx={{
                        mt: 1,
                        height: 6,
                        borderRadius: 3,
                        backgroundColor: '#e0e0e0',
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: '#2196f3',
                          borderRadius: 3
                        }
                      }}
                    />
                  </Box>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Monthly Leave Trend */}
      {monthlyTrend.length > 0 && (
        <Card sx={{ borderRadius: 2 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
              Monthly Leave Trend
            </Typography>
            <Grid container spacing={2}>
              {monthlyTrend.map((item, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Box sx={{
                    p: 2,
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                    textAlign: 'center'
                  }}>
                    <Typography variant="h6" fontWeight={700} color="primary">
                      {item.days}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {item.month}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {item.requests} requests
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {utilizationByType.length === 0 && monthlyTrend.length === 0 && (
        <Card sx={{ borderRadius: 2 }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <LeaveIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Leave Data Available
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Leave requests and utilization data will appear here once employees start requesting leave.
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default LeaveAnalytics;
